import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface TimerState {
  remainingTimeSeconds: number
  totalDurationMinutes: number | null
  isRunning: boolean
  isPaused: boolean
  lastSyncTime: string | null
  disconnectCount: number
  pauseCount: number
  warningShown: boolean
  criticalWarningShown: boolean
}

// Load timer state from localStorage if available
const loadTimerState = (): TimerState => {
  try {
    const saved = localStorage.getItem('timerState')
    //console.log('Loading timer state from localStorage:', saved)
    if (saved) {
      const parsed = JSON.parse(saved)
      //console.log('Parsed timer state:', parsed)
      // Only restore if we're on actual test page (not instructions or system-check) and have valid remaining time
      const isOnActualTestPage = window.location.pathname.includes('/test/') && 
        !window.location.pathname.includes('/instructions') && 
        !window.location.pathname.includes('/system-check')
      if (isOnActualTestPage && parsed.remainingTimeSeconds > 0) {
        //console.log('Restoring timer state from localStorage for test page')
        return {
          ...parsed,
          // Don't restore isRunning to prevent auto-start on page load
          isRunning: false,
          isPaused: false,
        }
      } else {
        //console.log('Not on test page or no remaining time, clearing timer state')
        localStorage.removeItem('timerState')
      }
    }
  } catch (error) {
    console.error('Failed to load timer state from localStorage:', error)
  }
  
  //console.log('Using default timer state')
  return {
    remainingTimeSeconds: 0,
    totalDurationMinutes: null,
    isRunning: false,
    isPaused: false,
    lastSyncTime: null,
    disconnectCount: 0,
    pauseCount: 0,
    warningShown: false,
    criticalWarningShown: false,
  }
}

const initialState: TimerState = loadTimerState()
const isDevelopment = typeof import.meta !== 'undefined' && import.meta.env?.MODE === 'development'

const timerSlice = createSlice({
  name: 'timer',
  initialState,
  reducers: {
    startTimer: (state, action: PayloadAction<{ remainingTimeSeconds: number; totalDurationMinutes?: number }>) => {
      //console.log('[Timer] Starting timer with:', action.payload)
      state.remainingTimeSeconds = action.payload.remainingTimeSeconds
      if (action.payload.totalDurationMinutes) {
        state.totalDurationMinutes = action.payload.totalDurationMinutes
      }
      state.isRunning = true
      state.isPaused = false
      state.lastSyncTime = new Date().toISOString()
      // Reset disconnect count when starting timer
      state.disconnectCount = 0
    },
    
    pauseTimer: (state) => {
      state.isRunning = false
      state.isPaused = true
      state.pauseCount += 1
    },
    
    resumeTimer: (state) => {
      state.isRunning = true
      state.isPaused = false
      state.lastSyncTime = new Date().toISOString()
    },
    
    stopTimer: (state) => {
      state.isRunning = false
      state.isPaused = false
      state.remainingTimeSeconds = 0
    },
    
    updateTimer: (state, action: PayloadAction<{ remainingTimeSeconds: number; lastSyncTime: string }>) => {
      const newTime = action.payload.remainingTimeSeconds
      
      // Validation: Don't accept time updates that are significantly larger than current time
      // Allow increases of up to 10 seconds (for minor clock drift), but not huge jumps
      if (state.remainingTimeSeconds > 0 && newTime > state.remainingTimeSeconds + 10) {
        console.warn(`Rejecting timer update: current=${state.remainingTimeSeconds}s, new=${newTime}s - increase too large`)
        return
      }
      
      // Validation: If we have totalDurationMinutes, ensure new time doesn't exceed it
      if (state.totalDurationMinutes && newTime > state.totalDurationMinutes * 60 + 10) {
        console.warn(`Rejecting timer update: new time ${newTime}s exceeds total duration ${state.totalDurationMinutes}min`)
        return
      }
      
      state.remainingTimeSeconds = newTime
      state.lastSyncTime = action.payload.lastSyncTime
      
      // Reset warnings if time increased (e.g., after reconnection)
      if (state.remainingTimeSeconds > 300) { // 5 minutes
        state.warningShown = false
        state.criticalWarningShown = false
      }
    },
    
    tick: (state) => {
      if (state.isRunning && state.remainingTimeSeconds > 0) {
        state.remainingTimeSeconds -= 1
      }
      
      // Don't stop timer here - let the Timer component handle auto-submit first
      // The timer will be stopped after submission
    },
    
    handleDisconnect: (state) => {
      state.disconnectCount += 1
    },
    
    syncWithServer: (state, action: PayloadAction<{
      remainingTimeSeconds: number
      pauseCount: number
      disconnectCount: number
      lastSyncTime: string
    }>) => {
      const { remainingTimeSeconds, pauseCount, disconnectCount, lastSyncTime } = action.payload
      
      // Validation: Don't accept sync that increases time significantly
      if (state.remainingTimeSeconds > 0 && remainingTimeSeconds > state.remainingTimeSeconds + 10) {
        console.warn(`Rejecting server sync: current=${state.remainingTimeSeconds}s, new=${remainingTimeSeconds}s`)
        return
      }
      
      // Validation: Check against total duration if available
      if (state.totalDurationMinutes && remainingTimeSeconds > state.totalDurationMinutes * 60 + 10) {
        console.warn(`Rejecting server sync: time ${remainingTimeSeconds}s exceeds total duration ${state.totalDurationMinutes}min`)
        return
      }
      
      state.remainingTimeSeconds = remainingTimeSeconds
      state.pauseCount = pauseCount
      state.disconnectCount = disconnectCount
      state.lastSyncTime = lastSyncTime
    },
    
    showWarning: (state, action: PayloadAction<'warning' | 'critical'>) => {
      if (action.payload === 'warning') {
        state.warningShown = true
      } else if (action.payload === 'critical') {
        state.criticalWarningShown = true
      }
    },
    
    resetTimer: (state) => {
      // Clear localStorage when resetting
      localStorage.removeItem('timerState')
      //console.log('Timer reset - cleared localStorage')
      return {
        remainingTimeSeconds: 0,
        totalDurationMinutes: null,
        isRunning: false,
        isPaused: false,
        lastSyncTime: null,
        disconnectCount: 0,
        pauseCount: 0,
        warningShown: false,
        criticalWarningShown: false,
      }
    },
  },
})

// Middleware to save timer state to localStorage
const PERSIST_ACTIONS = new Set([
  'timer/startTimer',
  'timer/pauseTimer',
  'timer/resumeTimer',
  'timer/stopTimer',
  'timer/updateTimer',
  'timer/syncWithServer',
  'timer/resetTimer',
])

let lastTickPersist = 0
const TICK_PERSIST_INTERVAL = 15000 // 15 seconds

export const timerMiddleware = (store: any) => (next: any) => (action: any) => {
  const result = next(action)

  if (!action?.type?.startsWith?.('timer/')) {
    return result
  }

  const shouldPersist = 
    PERSIST_ACTIONS.has(action.type) ||
    (action.type === 'timer/tick' && Date.now() - lastTickPersist >= TICK_PERSIST_INTERVAL)

  if (!shouldPersist) {
    return result
  }

  const timerState = store.getState().timer

  if (action.type === 'timer/tick') {
    lastTickPersist = Date.now()
  }

  if (isDevelopment) {
    //console.log('Saving timer state to localStorage:', action.type, timerState)
  }

  try {
    localStorage.setItem('timerState', JSON.stringify(timerState))
  } catch (error) {
    console.error('Failed to save timer state to localStorage:', error)
  }

  return result
}

export const {
  startTimer,
  pauseTimer,
  resumeTimer,
  stopTimer,
  updateTimer,
  tick,
  handleDisconnect,
  syncWithServer,
  showWarning,
  resetTimer,
} = timerSlice.actions

export default timerSlice.reducer
